import { type FormEvent, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import AuthLayout from '../components/auth/AuthLayout';
import { useAuth } from '../contexts/AuthProvider';
import { translateAuthError } from '../lib/authErrors';
import { useDocumentMeta } from '../hooks/useDocumentMeta';

const EMBER_BTN =
  'w-full inline-flex items-center justify-center gap-2 rounded-xl bg-ember px-4 py-3 font-semibold text-ink-950 shadow-[0_0_24px_-10px_#F26A4B] transition-all duration-200 hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed';
const ERR_BOX =
  'rounded-xl bg-risk-high/10 border border-risk-high/30 text-risk-high text-sm p-3';

export default function Entrar() {
  useDocumentMeta('Entrar | Tutela', undefined, undefined, { noindex: true });
  const { signInWithPassword, configured } = useAuth();
  const navigate = useNavigate();
  const location = useLocation() as { state?: { from?: string } };
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const redirectTo = location.state?.from ?? '/ignis';

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    const { error } = await signInWithPassword(email.trim(), password);
    setLoading(false);
    if (error) {
      setError(translateAuthError(error));
      return;
    }
    navigate(redirectTo, { replace: true });
  }

  return (
    <AuthLayout
      title="Entrar"
      subtitle="Acesso reservado a organizações aprovadas."
      footer={
        <>
          Ainda não tem conta?{' '}
          <Link to="/registar" className="text-ember hover:underline">
            Registar
          </Link>
        </>
      }
    >
      {!configured && (
        <p className={`mb-4 ${ERR_BOX}`}>Autenticação indisponível nesta configuração.</p>
      )}
      <form onSubmit={onSubmit} className="space-y-4">
        <div>
          <label className="label-dark" htmlFor="email">
            Email
          </label>
          <input
            id="email"
            type="email"
            autoComplete="email"
            required
            className="input-dark"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="nome@organizacao.pt"
          />
        </div>
        <div>
          <div className="flex items-baseline justify-between">
            <label className="label-dark" htmlFor="password">
              Palavra-passe
            </label>
            <Link to="/recuperar" className="text-xs text-mist-muted hover:text-ember transition-colors">
              Esqueceu-se da palavra-passe?
            </Link>
          </div>
          <input
            id="password"
            type="password"
            autoComplete="current-password"
            required
            className="input-dark"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
          />
        </div>
        {error && <p className={ERR_BOX}>{error}</p>}
        <button type="submit" disabled={loading || !configured} className={EMBER_BTN}>
          {loading ? 'A entrar...' : 'Entrar'}
        </button>
      </form>
    </AuthLayout>
  );
}
